import client from './inventory-request';

function ss(key) {
  const v = sessionStorage.getItem(key);
  return v && v !== 'null' && v !== 'undefined' ? v : null;
}

function requisitionScopeParams(opts = {}) {
  const params = {};
  if (opts.tenant_id) params.tenant_id = opts.tenant_id;
  if (opts.hospital_id) params.hospital_id = opts.hospital_id;
  return Object.keys(params).length ? params : undefined;
}

function requisitionActionBody(opts = {}) {
  const body = {};
  const actionBy = opts.action_by || ss('USER_ACCESS_KEY');
  if (actionBy) body.action_by = actionBy;
  if (opts.reason) body.reason = opts.reason;
  // Partial approve: [{ id: requisition-line-uuid, approved_quantity: "5" }]
  // Omit items for full approve (all lines at requested qty).
  if (Array.isArray(opts.items) && opts.items.length) body.items = opts.items;
  return body;
}

// API spec: acknowledge wants { acknowledged_by, trigger_billing_reversal }, no remarks/reason.
function acknowledgeReturnBody(opts = {}) {
  const body = {};
  const actor = opts.acknowledged_by || ss('USER_ACCESS_KEY');
  if (actor) body.acknowledged_by = actor;
  body.trigger_billing_reversal = Boolean(opts.trigger_billing_reversal);
  return body;
}

// API spec: reject wants { rejected_by, reason } only — no remarks.
function rejectReturnBody(opts = {}) {
  const body = {};
  const actor = opts.rejected_by || ss('USER_ACCESS_KEY');
  if (actor) body.rejected_by = actor;
  if (opts.reason) body.reason = opts.reason;
  return body;
}

// Requisitions
// API: GET /inventory/dispensing/requisitions/
export const getRequisitions           = (p) => client.get('/inventory/dispensing/requisitions/', p);
// API: GET /inventory/dispensing/requisitions/{id}/
export const getRequisition            = (id) => client.get(`/inventory/dispensing/requisitions/${id}/`);
// API: POST /inventory/dispensing/requisitions/
export const createRequisition         = (body) => client.post('/inventory/dispensing/requisitions/', body);
// API: PATCH /inventory/dispensing/requisitions/{id}/
export const updateRequisition         = (id, body) => client.patch(`/inventory/dispensing/requisitions/${id}/`, body);
// API: POST /inventory/dispensing/requisitions/{id}/submit/ — body must be {}
export const submitRequisition         = (id, opts = {}) =>
  client.post(`/inventory/dispensing/requisitions/${id}/submit/`, {}, requisitionScopeParams(opts));
// API: POST /inventory/dispensing/requisitions/{id}/approve/
export const approveRequisition        = (id, opts = {}) =>
  client.post(`/inventory/dispensing/requisitions/${id}/approve/`, requisitionActionBody(opts), requisitionScopeParams(opts));
// API: POST /inventory/dispensing/requisitions/{id}/reject/
export const rejectRequisition         = (id, opts = {}) =>
  client.post(`/inventory/dispensing/requisitions/${id}/reject/`, requisitionActionBody(opts), requisitionScopeParams(opts));
// API: GET /inventory/dispensing/requisitions/{id}/items/
export const getRequisitionItems       = (id) => client.get(`/inventory/dispensing/requisitions/${id}/items/`);
// API: DELETE /inventory/dispensing/requisitions/{id}/ — soft delete; allowed on DRAFT, responds status=CANCELLED
// NOTE: as of 2026-07-10 the EMR gateway 405s this (Allow: GET, PATCH, HEAD, OPTIONS) even
// though the underlying dispensing-billing-service correctly supports DELETE directly
// (verified via Swagger against htinventory-dev.hlthtek.com). This is a gateway routing gap,
// not a frontend bug — report to the gateway/platform team; do not "fix" this with a PATCH,
// since status is not a documented writable field on the plain update endpoint.
export const deleteRequisition         = (id, opts = {}) =>
  client.delete(`/inventory/dispensing/requisitions/${id}/`, requisitionScopeParams(opts));
// API: POST /inventory/dispensing/requisitions/{id}/release-reservation/ — body must be {}
export const releaseRequisitionReservation = (id, opts = {}) =>
  client.post(`/inventory/dispensing/requisitions/${id}/release-reservation/`, {}, requisitionScopeParams(opts));
// API: GET /inventory/dispensing/requisitions/{id}/issues/
export const getRequisitionIssues      = (id, opts = {}) =>
  client.get(`/inventory/dispensing/requisitions/${id}/issues/`, requisitionScopeParams(opts));
// API: GET /inventory/dispensing/requisitions/{id}/shortage-check/
// Returns per-line central available_quantity for issue planning.
export const getRequisitionShortageCheck = (id, opts = {}) =>
  client.get(`/inventory/dispensing/requisitions/${id}/shortage-check/`, requisitionScopeParams(opts));

// Issues
// API: GET /inventory/dispensing/issues/
export const getIssues                 = (p) => client.get('/inventory/dispensing/issues/', p);
// API: GET /inventory/dispensing/issues/pending/ — requisitions awaiting issue at central (to_store_id)
// Statuses: APPROVED | RESERVED | PARTIALLY_ISSUED
export const getPendingIssues          = (p) => client.get('/inventory/dispensing/issues/pending/', p);
// API: GET /inventory/dispensing/issues/{id}/
export const getIssue                  = (id) => client.get(`/inventory/dispensing/issues/${id}/`);
// API: POST /inventory/dispensing/issues/
export const createIssue               = (body) => client.post('/inventory/dispensing/issues/', body);
// API: PATCH /inventory/dispensing/issues/{id}/
export const updateIssue               = (id, body) => client.patch(`/inventory/dispensing/issues/${id}/`, body);
// API: POST /inventory/dispensing/issues/from-requisition/{requisitionId}/ — supports partial issue
export const createIssueFromRequisition = (requisitionId, body, opts = {}) =>
  client.post(`/inventory/dispensing/issues/from-requisition/${requisitionId}/`, body, requisitionScopeParams(opts));
// API: POST /inventory/dispensing/issues/{id}/confirm/ — status → ISSUED; physical stock moves on acknowledge only
export const confirmIssue              = (id, opts = {}) =>
  client.post(`/inventory/dispensing/issues/${id}/confirm/`, {
    confirmed_by: opts.confirmed_by || ss('USER_ACCESS_KEY'),
    remarks: opts.remarks || '',
  }, requisitionScopeParams(opts));
// API: POST /inventory/dispensing/issues/{id}/acknowledge/ — receiver accepts/rejects; stock transfers here
export const acknowledgeIssue          = (id, body = {}, opts = {}) =>
  client.post(`/inventory/dispensing/issues/${id}/acknowledge/`, {
    acknowledged_by: body.acknowledged_by || ss('USER_ACCESS_KEY'),
    remarks: body.remarks || '',
    line_items: body.line_items || [],
  }, requisitionScopeParams(opts));
// API: GET /inventory/dispensing/issues/{id}/items/
export const getIssueItems             = (id) => client.get(`/inventory/dispensing/issues/${id}/items/`);

// ---------------------------------------------------------------------------
// Pharmacy dispense lifecycle (Create → Verify → Reserve → Allocate → Deliver)
// Gateway: /app/api/inventory/pharmacy/dispensing/...
// Doc example /api/v1/dispensing maps here via EMR proxy.
// ---------------------------------------------------------------------------

const PHARMACY_DISPENSE = '/inventory/pharmacy/dispensing';

function dispensingScopeParams(opts = {}) {
  const params = {};
  if (opts.tenant_id) params.tenant_id = opts.tenant_id;
  if (opts.hospital_id) params.hospital_id = opts.hospital_id;
  return Object.keys(params).length ? params : undefined;
}

function actorId(explicit) {
  if (explicit) return explicit;
  return ss('USER_ACCESS_KEY');
}

// API: GET /inventory/pharmacy/dispensing/
export const getDispenses = (p) => client.get(`${PHARMACY_DISPENSE}/`, p);
// API: GET /inventory/pharmacy/dispensing/{transaction_id}/
export const getDispense = (id, opts = {}) =>
  client.get(`${PHARMACY_DISPENSE}/${id}/`, dispensingScopeParams(opts));
// API: POST /inventory/pharmacy/dispensing/
export const createDispense = (body) => client.post(`${PHARMACY_DISPENSE}/`, body);
// API: PATCH /inventory/pharmacy/dispensing/{transaction_id}/
export const updateDispense = (id, body, opts = {}) =>
  client.patch(`${PHARMACY_DISPENSE}/${id}/`, body, dispensingScopeParams(opts));
// API: GET /inventory/pharmacy/dispensing/{transaction_id}/items/
export const getDispenseItems = (id, opts = {}) =>
  client.get(`${PHARMACY_DISPENSE}/${id}/items/`, dispensingScopeParams(opts));

// API: POST /inventory/pharmacy/dispensing/{transaction_id}/verify/
export const verifyDispense = (id, body = {}, opts = {}) =>
  client.post(`${PHARMACY_DISPENSE}/${id}/verify/`, {
    verified_by: actorId(body.verified_by),
    verification_notes: body.verification_notes || '',
    allergy_checked: Boolean(body.allergy_checked),
    dose_checked: Boolean(body.dose_checked),
  }, dispensingScopeParams(opts));

// API: POST /inventory/pharmacy/dispensing/{transaction_id}/reserve/
export const reserveDispense = (id, body = {}, opts = {}) =>
  client.post(`${PHARMACY_DISPENSE}/${id}/reserve/`, body, dispensingScopeParams(opts));

// API: POST /inventory/pharmacy/dispensing/{transaction_id}/allocate/
export const allocateDispense = (id, body = {}, opts = {}) =>
  client.post(`${PHARMACY_DISPENSE}/${id}/allocate/`, {
    allocated_by: actorId(body.allocated_by),
    allocation_notes: body.allocation_notes || '',
  }, dispensingScopeParams(opts));

// API: POST /inventory/pharmacy/dispensing/{transaction_id}/deliver/
export const deliverDispense = (id, body = {}, opts = {}) =>
  client.post(`${PHARMACY_DISPENSE}/${id}/deliver/`, {
    delivered_by: actorId(body.delivered_by),
    handover_to: body.handover_to,
  }, dispensingScopeParams(opts));

// API: POST /inventory/pharmacy/dispensing/direct/
export const createDirectDispense = (body) =>
  client.post(`${PHARMACY_DISPENSE}/direct/`, body);

// API: POST /inventory/pharmacy/dispensing/{transaction_id}/confirm/
export function confirmReserveDispense(transactionId, body = {}, opts = {}) {
  if (!transactionId) {
    return Promise.reject(new Error('transaction id is required to confirm dispense'));
  }
  return client.post(
    `${PHARMACY_DISPENSE}/${encodeURIComponent(transactionId)}/confirm/`,
    body,
    dispensingScopeParams(opts),
  );
}

/** Finalize reserved dispense — prefer deliverDispense for the new lifecycle. */
export const confirmDispense = (id, body = {}, opts = {}) => {
  if (body.handover_to) {
    return deliverDispense(id, body, opts);
  }
  return confirmReserveDispense(id, body, opts);
};

// Consumption
// API: POST /inventory/dispensing/consumption/
export const createConsumption         = (body) => client.post('/inventory/dispensing/consumption/', body);
// API: GET /inventory/dispensing/consumption/
export const getConsumptions           = (p) => client.get('/inventory/dispensing/consumption/', p);
// API: GET /inventory/dispensing/consumption/{id}/
export const getConsumption            = (id) => client.get(`/inventory/dispensing/consumption/${id}/`);
// API: GET /inventory/dispensing/consumption/{id}/items/
export const getConsumptionItems       = (id) => client.get(`/inventory/dispensing/consumption/${id}/items/`);
// API: POST /inventory/dispensing/consumption/{id}/confirm/
export const confirmConsumption        = (id, opts = {}) =>
  client.post(`/inventory/dispensing/consumption/${id}/confirm/`, {
    confirmed_by: opts.confirmed_by || ss('USER_ACCESS_KEY'),
    remarks: opts.remarks || '',
  });

// Returns
// API: POST /inventory/dispensing/returns/
export const createReturn              = (body, opts = {}) =>
  client.post('/inventory/dispensing/returns/', body, requisitionScopeParams(opts));
// API: GET /inventory/dispensing/returns/
export const getReturns                = (p) => client.get('/inventory/dispensing/returns/', p);
// API: GET /inventory/dispensing/returns/{id}/
export const getReturn                 = (id) => client.get(`/inventory/dispensing/returns/${id}/`);
// API: GET /inventory/dispensing/returns/{id}/items/
export const getReturnItems            = (id) => client.get(`/inventory/dispensing/returns/${id}/items/`);
// API: POST /inventory/dispensing/returns/{id}/confirm/ — credits stock back, optionally reverses billing
export const confirmReturn             = (id, opts = {}) =>
  client.post(`/inventory/dispensing/returns/${id}/confirm/`, {
    confirmed_by: opts.confirmed_by || ss('USER_ACCESS_KEY'),
    trigger_billing_reversal: Boolean(opts.trigger_billing_reversal),
    remarks: opts.remarks || '',
  }, requisitionScopeParams(opts));
// API: POST /inventory/dispensing/returns/{id}/acknowledge/
export const acknowledgeReturn         = (id, opts = {}) =>
  client.post(`/inventory/dispensing/returns/${id}/acknowledge/`, acknowledgeReturnBody(opts), requisitionScopeParams(opts));
// API: POST /inventory/dispensing/returns/{id}/reject/ — reason required
export const rejectReturn              = (id, opts = {}) =>
  client.post(`/inventory/dispensing/returns/${id}/reject/`, rejectReturnBody(opts), requisitionScopeParams(opts));

// Billing
// API: GET /inventory/dispensing/billing/pending/
export const getPendingBilling         = (p) => client.get('/inventory/dispensing/billing/pending/', p);
// API: POST /inventory/dispensing/billing/{id}/post/
export const postBilling               = (id, body) => client.post(`/inventory/dispensing/billing/${id}/post/`, body);
// API: POST /inventory/dispensing/billing/{id}/reverse/
export const reverseBilling            = (id, body) => client.post(`/inventory/dispensing/billing/${id}/reverse/`, body);

// Batch / Internal Trace
// API: GET /inventory/dispensing/transactions/{txnId}/batch-trace/
export const getBatchTrace             = (txnId) => client.get(`/inventory/dispensing/transactions/${txnId}/batch-trace/`);
// API: GET /inventory/dispensing/patients/{patientId}/batch-exposure/
export const getPatientBatchExposure   = (patientId, admissionId) =>
  client.get(`/inventory/dispensing/patients/${patientId}/batch-exposure/`, { admission_id: admissionId });
// API: GET /inventory/dispensing/batches/{batchNumber}/patient-exposure/
export const getBatchPatientExposure   = (batchNumber, itemId) =>
  client.get(`/inventory/dispensing/batches/${batchNumber}/patient-exposure/`, { item_id: itemId });

// Prescriptions — see api/pharmacy.js
export { getPrescriptions } from './pharmacy';
